import { ModelBase } from '../abstract/model-base.model';
import { PhoneAreaCode } from '../address/phone-area-code.model';
import { CustomProfile } from '../authorization/custom-profile.model';
import { LegalStatusEnum } from '../../enums/legal-status.enum';

export class User extends ModelBase {

    static override CLASS_NAME: string = 'User';

    private _announcementPopupsEnabled!: boolean;
    private _authorizations!: string[];
    private _authorizationsRollback!: string[];
    private _avatar!: string;
    private _avatarIcon!: string;
    private _avatarLabelColor!: string;
    private _canViewMonitoring!: boolean;
    private _email!: string;
    private _firstName!: string;
    private _isStaff!: boolean;
    private _lastName!: string;
    private _mfa!: boolean;
    private _phone1!: string;
    private _phone1AreaCode?: PhoneAreaCode;
    private _phone2!: string;
    private _phone2AreaCode?: PhoneAreaCode;
    private _cguStatus!: LegalStatusEnum;
    private _privacyStatus!: LegalStatusEnum;
    private _simulatedProfile: CustomProfile | undefined;

    constructor(data?: any) {
        super(data);

        if (!this._authorizations) {
            this._authorizations = [];
            this._authorizationsRollback = [];
        }
    }

    public override toString(): string {
        return this.fullName;
    }

    public hasAuthorization(authorization: string): boolean {
        return this.authorizations.includes(authorization);
    }

    public simulateOtherProfile(profile: CustomProfile): void {
        this._simulatedProfile = profile;
        this._authorizationsRollback = [...this.authorizations];
        this.authorizations = profile.authorizations.map(authorization => authorization.code);
    }

    public stopProfileSimulation(): void {
        if (this._authorizationsRollback) {
            this._simulatedProfile = undefined;
            this._authorizations = [...this._authorizationsRollback];
            this._authorizationsRollback = [];
        }
    }

    /**
     * L'utilisateur accepte-t-il les annonces en popup ? Les annonces critiques
     * s'affichent malgré un refus : la règle est appliquée côté serveur, qui livre le
     * mode d'affichage effectif de chaque annonce.
     */
    public get announcementPopupsEnabled(): boolean {
        return this._announcementPopupsEnabled;
    }

    public set announcementPopupsEnabled(value: any) {
        this._setValue('_announcementPopupsEnabled', value, 'boolean');
    }

    public get authorizations(): string[] {
        return this._authorizations;
    }

    public set authorizations(value: any) {
        this._setValue('_authorizations', value, 'string');
    }

    public get authorizationsRollback(): string[] {
        return this._authorizationsRollback;
    }

    public set authorizationsRollback(value: any) {
        this._setValue('_authorizations', value, 'string');
    }

    public set avatar(value: any) {
        this._setValue('_avatar', value, 'string');
    }

    public get avatar(): string {
        return this._avatar;
    }

    public get avatarIcon(): string {
        if (!this.avatar && !this.avatarLabel) {
            return !!this._avatarIcon ? this._avatarIcon : 'pi pi-user';
        } else {
            return '';
        }
    }

    public set avatarIcon(value: any) {
        this._setValue('_avatarIcon', value, 'string');
    }

    public get avatarLabel(): string {
        if (!this.avatar) {
            if (!!this.firstName && !!this.lastName) {
                return `${this.firstName.toUpperCase()[0]}${this.lastName.toUpperCase()[0]}`;
            } else if (!!this.firstName) {
                return this.firstName.toUpperCase()[0];
            } else if (!!this.lastName) {
                return this.lastName.toUpperCase()[0];
            }
        }
        return '';
    }

    public set avatarLabelColor(value: any) {
        this._setValue('_avatarLabelColor', value, 'string');
    }

    public get avatarLabelColor(): string {
        return this._avatarLabelColor;
    }

    public get canViewMonitoring(): boolean {
        return this._canViewMonitoring;
    }

    public set canViewMonitoring(value: any) {
        this._setValue('_canViewMonitoring', value, 'boolean');
    }

    public get email(): string {
        return this._email;
    }

    public set email(value: any) {
        this._setValue('_email', value, 'string');
    }

    public get firstName(): string {
        return this._firstName;
    }

    public set firstName(value: any) {
        this._setValue('_firstName', value, 'string');
    }

    public get fullName(): string {
        return this.firstName + ' ' + this.lastName;
    }

    public get isSimulatingProfile(): boolean {
        return this._authorizationsRollback && this._authorizationsRollback.length > 0;
    }

    public get isStaff(): boolean {
        return this._isStaff;
    }

    public set isStaff(value: any) {
        this._setValue('_isStaff', value, 'boolean');
    }

    public get lastName(): string {
        return this._lastName;
    }

    public set lastName(value: any) {
        this._setValue('_lastName', value, 'string');
    }

    public get mfa(): boolean {
        return this._mfa;
    }

    public set mfa(value: any) {
        this._setValue('_mfa', value, 'boolean');
    }

    public get phone1(): string {
        return this._phone1;
    }

    public set phone1(value: any) {
        this._setValue('_phone1', value, 'string');
    }

    public get phone1AreaCode(): PhoneAreaCode | undefined {
        return this._phone1AreaCode;
    }

    public set phone1AreaCode(value: any) {
        this._setValue('_phone1AreaCode', value, 'PhoneAreaCode');
    }

    public get phone2(): string {
        return this._phone2;
    }

    public set phone2(value: any) {
        this._setValue('_phone2', value, 'string');
    }

    public get phone2AreaCode(): PhoneAreaCode | undefined {
        return this._phone2AreaCode;
    }

    public set phone2AreaCode(value: any) {
        this._setValue('_phone2AreaCode', value, 'PhoneAreaCode');
    }

    public get simulatedProfile(): CustomProfile | undefined {
        return this._simulatedProfile;
    }

    public get cguStatus(): LegalStatusEnum {
        return this._cguStatus;
    }

    public set cguStatus(value: any) {
        this._setValue('_cguStatus', value, 'string');
    }

    public get privacyStatus(): LegalStatusEnum {
        return this._privacyStatus;
    }

    public set privacyStatus(value: any) {
        this._setValue('_privacyStatus', value, 'string');
    }
}
